import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, ShieldCheck, RefreshCw, ChevronRight, Search, X, ChevronLeft, ChevronsLeft, ChevronsRight, Undo2, CheckCircle2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";

const PERM_LABEL = {
  admin_access: "Admin-Zugriff",
  news_write: "News schreiben",
  news_review: "News freigeben",
  news_sticky: "News pinnen",
  events_edit: "Events verwalten",
  projects_edit: "Projekte verwalten",
  badges_edit: "Badges verwalten",
  rares_edit: "Rares verwalten",
  comments_moderate: "Kommentare moderieren",
  media_access: "Medien-Bibliothek",
  ai_import: "KI-News-Import",
  furniture_edit: "Furniture verwalten",
};

const ACTION_LABEL = {
  "permissions_changed": "Berechtigungen geaendert",
  "permissions_changed.undo": "Berechtigungen zurueckgesetzt",
  "article.toggle_publish": "Artikel online/offline",
  "article.toggle_publish.undo": "Artikel-Status zurueckgesetzt",
  "article.sticky": "Sticky toggle",
  "article.sticky.undo": "Sticky zurueckgesetzt",
  "article.create": "Artikel erstellt",
  "article.update": "Artikel bearbeitet",
  "article.delete": "Artikel geloescht",
  "settings.update": "Einstellungen geaendert",
  "rare.create": "Rare erstellt",
  "rare.update": "Rare bearbeitet",
  "rare.delete": "Rare geloescht",
  "event.create": "Event erstellt",
  "event.update": "Event bearbeitet",
  "event.delete": "Event geloescht",
  "badge.create": "Badge erstellt",
  "badge.update": "Badge bearbeitet",
  "badge.delete": "Badge geloescht",
};

// Welche Aktionen sind vom Backend wirklich rueckgaengig machbar?
const UNDOABLE = new Set(["permissions_changed", "article.toggle_publish", "article.sticky"]);

const hasFromTo = (changes) => {
  if (!changes || typeof changes !== "object") return false;
  return Object.values(changes).some((v) => v && typeof v === "object" && "from" in v && "to" in v);
};

const PAGE_SIZE = 100;

const formatDateTime = (d) => d ? new Date(d).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const AuditLogPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(1);
  const [undoingId, setUndoingId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await a.listAuditLog({
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        q: qDebounced,
        action: actionFilter,
      });
      setEntries(r.items || []);
      setTotal(r.total || 0);
      if (Array.isArray(r.actions) && r.actions.length) setActions(r.actions);
    } finally { setLoading(false); }
  }, [a, page, qDebounced, actionFilter]);

  // Debounced search (300ms)
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Bei neuer Suche/Filter zurueck auf Seite 1
  useEffect(() => { setPage(1); }, [qDebounced, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setQ("");
    setActionFilter("");
  };

  const onUndo = async (entry) => {
    const label = ACTION_LABEL[entry.action || entry.type] || entry.action;
    if (!window.confirm(`Diese Aktion rueckgaengig machen?\n\n${label}\nZiel: ${entry.target_username || entry.target_label || entry.target_id || "?"}`)) return;
    setUndoingId(entry.id);
    try {
      await a.undoAuditEntry(entry.id);
      toast({ title: "Rueckgaengig gemacht", description: `${label} wurde zurueckgesetzt.` });
      await load();
    } catch (err) {
      toast({
        title: "Fehler",
        description: err?.response?.data?.detail || "Undo fehlgeschlagen",
        variant: "destructive",
      });
    } finally {
      setUndoingId(null);
    }
  };

  const renderChanges = (e) => {
    const changes = e.changes || {};
    const action = e.action || e.type || "";
    // Permissions-Aenderungen
    if (action === "permissions_changed" || e.type === "permissions_changed") {
      return Object.entries(changes).map(([k, v]) => (
        <span key={k} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] ${v?.to ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border border-rose-500/30 bg-rose-500/10 text-rose-300"}`}>
          {v?.to ? "+" : "−"} {PERM_LABEL[k] || k}
        </span>
      ));
    }
    // Generic changes: {field: value} or {field: {from, to}}
    return Object.entries(changes).map(([k, v]) => {
      const isFromTo = v && typeof v === "object" && ("to" in v || "from" in v);
      const label = isFromTo
        ? `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`
        : `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`;
      return (
        <span key={k} className="inline-flex items-center gap-1 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-zinc-300 font-mono max-w-md truncate" title={label}>
          {label}
        </span>
      );
    });
  };

  const hasFilters = !!q || !!actionFilter;
  const fromIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#b39bff]" /> Audit-Log
          </h2>
          <p className="mt-1 text-sm text-zinc-400">
            Aenderungen an Berechtigungen, Artikeln und Einstellungen. Maximal 100 Eintraege pro Seite.
          </p>
        </div>
        <Button onClick={load} className="btn-ghost rounded-lg px-3 py-2 text-sm" data-testid="audit-log-refresh">
          <RefreshCw className="mr-2 h-4 w-4" /> Aktualisieren
        </Button>
      </div>

      {/* Filter-Bar */}
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-[#0f141b] p-3 md:grid-cols-[1fr_240px_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suchen: Username, Aktion, Ziel-Titel, IDs ..."
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200"
            data-testid="audit-log-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
              title="Suche leeren"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
          data-testid="audit-log-action-filter"
        >
          <option value="">Alle Aktionen ({total})</option>
          {actions.map((act) => (
            <option key={act} value={act}>{ACTION_LABEL[act] || act}</option>
          ))}
        </select>
        {hasFilters && (
          <Button
            onClick={resetFilters}
            className="btn-ghost rounded-lg px-3 py-2 text-xs whitespace-nowrap"
            data-testid="audit-log-reset"
          >
            <X className="mr-1 h-3.5 w-3.5" /> Filter zuruecksetzen
          </Button>
        )}
      </div>

      {/* Zaehlerleiste */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
        <div>
          {total === 0 ? "Keine Treffer" : <>Eintraege <span className="text-zinc-300">{fromIdx}–{toIdx}</span> von <span className="text-zinc-300">{total.toLocaleString("de-DE")}</span></>}
        </div>
        <div>
          Seite <span className="text-zinc-300">{page}</span> / <span className="text-zinc-300">{totalPages}</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] p-10 text-center text-sm text-zinc-500">
          {hasFilters ? "Keine Eintraege mit diesen Filtern." : "Noch keine Audit-Eintraege."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]">
          {entries.map((e) => {
            const actionKey = e.action || e.type || "";
            const canUndo = UNDOABLE.has(actionKey) && hasFromTo(e.changes) && !e.undone_at;
            const isUndo = actionKey.endsWith(".undo");
            return (
            <div
              key={e.id}
              data-testid={`audit-entry-${e.id}`}
              className={`grid grid-cols-1 gap-2 border-b border-white/5 px-4 py-3 last:border-b-0 md:grid-cols-[160px_1fr_auto] ${e.undone_at ? "opacity-60" : ""}`}
            >
              <div className="text-xs text-zinc-500">{formatDateTime(e.created_at)}</div>
              <div className="text-sm text-zinc-200">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold text-[#b39bff]">{e.actor || "?"}</span>
                  <ChevronRight className="h-3 w-3 text-zinc-500" />
                  <span className="font-semibold text-white">
                    {e.target_username || e.target_label || e.target_id || "(unbekannt)"}
                  </span>
                  {actionKey && (
                    <span className={`ml-1 inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                      isUndo ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-white/10 bg-white/5 text-zinc-400"
                    }`}>
                      {isUndo && <Undo2 className="mr-0.5 h-2.5 w-2.5" />}
                      {ACTION_LABEL[actionKey] || actionKey}
                    </span>
                  )}
                  {e.undone_at && (
                    <span className="inline-flex items-center gap-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-300">
                      <CheckCircle2 className="h-2.5 w-2.5" /> bereits zurueckgesetzt
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {renderChanges(e)}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                {canUndo ? (
                  <Button
                    onClick={() => onUndo(e)}
                    disabled={undoingId === e.id}
                    data-testid={`audit-undo-${e.id}`}
                    className="btn-ghost rounded-md border border-amber-500/30 px-2.5 py-1 text-[11px] font-medium text-amber-300 hover:border-amber-500/60 hover:bg-amber-500/10"
                    title="Diese Aenderung rueckgaengig machen"
                  >
                    {undoingId === e.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><Undo2 className="mr-1 h-3 w-3" /> Rueckgaengig</>}
                  </Button>
                ) : null}
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {e.actor_role === "admin" ? "Admin" : "User"}
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2" data-testid="audit-log-pagination">
          <Button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="btn-ghost rounded-md px-2 py-1 text-xs"
            title="Erste Seite"
            data-testid="audit-page-first"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost rounded-md px-2 py-1 text-xs"
            data-testid="audit-page-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <select
            value={page}
            onChange={(e) => setPage(parseInt(e.target.value, 10))}
            className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1 text-xs text-zinc-200 min-w-[80px]"
            data-testid="audit-page-select"
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>Seite {p}</option>
            ))}
          </select>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-ghost rounded-md px-2 py-1 text-xs"
            data-testid="audit-page-next"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="btn-ghost rounded-md px-2 py-1 text-xs"
            title="Letzte Seite"
            data-testid="audit-page-last"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
